import type React from "react";
import { useEffect, useRef, useState } from "react";
import { TiMediaRewind } from "react-icons/ti";
import { TiMediaFastForward } from "react-icons/ti";
import { TiMediaPlay } from "react-icons/ti";
import { TiMediaPause } from "react-icons/ti";
import style from "./AudioPlayer.module.scss";

export const CustomAudioPlayer = ({ src }: { src: string }) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [volume, setVolume] = useState(1);
	const audioRef = useRef<HTMLAudioElement>(null);

	const togglePlayPause = () => {
		const prevValue = isPlaying;
		setIsPlaying(!prevValue);
		if (!prevValue) {
			audioRef?.current?.play();
		} else {
			audioRef?.current?.pause();
		}
	};

	const handleProgress = (event: React.ChangeEvent<HTMLInputElement>) => {
		const audio = audioRef.current;
		if (!audio) return;
		const time = (audio.duration / 100) * Number(event.target.value);
		audio.currentTime = time;
		setCurrentTime(time);
	};

	const handleVolume = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newVolume = Number(event.target.value);
		if (!audioRef.current) return;
		audioRef.current.volume = newVolume;
		setVolume(newVolume);
	};

	const seekForward = () => {
		if (!audioRef.current) return;
		audioRef.current.currentTime += 10;
	};

	const seekBackward = () => {
		if (!audioRef.current) return;
		audioRef.current.currentTime -= 10;
	};

	useEffect(() => {
		const audio = audioRef.current;
		const updateProgress = () => {
			if (!audio) return;
			setCurrentTime(audio.currentTime);
		};
		audio?.addEventListener("timeupdate", updateProgress);

		return () => audio?.removeEventListener("timeupdate", updateProgress);
	}, []);

	const getProgressBarStyle = (percentage: number) => ({
		background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #ccc ${percentage}%, #ccc 100%)`,
	});

	return (
		<div className={style.audioWrapper}>
			<audio ref={audioRef} src={src} hidden>
				<track kind="captions" src="captions.vtt" default />
			</audio>
			<button
				type="button"
				className={`${style.audioButton} ${style.playBtn}`}
				onClick={togglePlayPause}
			>
				{isPlaying ? <TiMediaPause /> : <TiMediaPlay />}
			</button>
			<button
				type="button"
				className={style.audioButton}
				onClick={seekBackward}
			>
				<TiMediaRewind />
			</button>
			{audioRef?.current && (
				<input
					type="range"
					className={style.audioRange}
					style={getProgressBarStyle(
						(currentTime / (audioRef.current?.duration || 1)) * 100,
					)}
					min="0"
					max="100"
					value={(currentTime / audioRef?.current?.duration || 0) * 100 || 0}
					onChange={handleProgress}
				/>
			)}
			<button type="button" className={style.audioButton} onClick={seekForward}>
				<TiMediaFastForward />
			</button>
			<input
				type="range"
				className={style.volumeControl}
				min="0"
				max="1"
				step="0.1"
				value={volume}
				onChange={handleVolume}
				style={getProgressBarStyle(volume * 100)}
			/>
		</div>
	);
};
